import React, { useState, useEffect, useRef } from 'react';
import GreekInput from '../../greek-revision/components/GreekInput';
import FormattingToolbar from './FormattingToolbar';
import { useAppContext } from '../context/RevisionContext';
import { Card, CardInput, FormattedText } from '../types/models';
import { RevisionSystemConfig } from '../config/RevisionSystemConfig';
import { storage, ref, uploadBytesResumable, getDownloadURL } from '../../firebase-config';
import '../../greek-revision/components/CardEditor.css';

interface CardEditorProps {
  config: RevisionSystemConfig;
  card?: Card; // If provided, edit mode; otherwise, create mode
  sectionId?: string; // Default section for new cards
  onSave: (card: CardInput) => Promise<void>;
  onCancel: () => void;
}

/**
 * CardEditor component for creating and editing revision cards
 * Conditionally includes GreekInput based on config.ui.includeGreekInput
 * Requirements: 2.1, 2.4, 1.4
 */
const CardEditor: React.FC<CardEditorProps> = ({
  config,
  card,
  sectionId: defaultSectionId,
  onSave,
  onCancel
}) => {
  const { sections } = useAppContext();
  
  // Form state
  const [frontText, setFrontText] = useState<string>('');
  const [backFormatted, setBackFormatted] = useState<FormattedText>({ plainText: '', html: '' });
  const [selectedSectionId, setSelectedSectionId] = useState<string>(defaultSectionId || '');
  const [tags, setTags] = useState<string>('');
  const [frontImages, setFrontImages] = useState<string[]>([]);
  const [backImages, setBackImages] = useState<string[]>([]);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const frontImageRef = useRef<HTMLInputElement>(null);
  const backImageRef = useRef<HTMLInputElement>(null);

  const uploadCardImage = (file: File, side: 'front' | 'back') => {
    const storageRef = ref(storage, `card-images/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    if (side === 'front') setUploadingFront(true);
    else setUploadingBack(true);
    task.on('state_changed', null,
      () => { if (side === 'front') setUploadingFront(false); else setUploadingBack(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        if (side === 'front') {
          setFrontImages(prev => [...prev, url]);
          setUploadingFront(false);
        } else {
          setBackImages(prev => [...prev, url]);
          setUploadingBack(false);
        }
      }
    );
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (file) uploadCardImage(file, side);
    e.target.value = '';
  };

  // Validation state
  const [errors, setErrors] = useState<{ front?: string; back?: string; section?: string; }>({});
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Initialize form with card data in edit mode
   */
  useEffect(() => {
    if (card) {
      setFrontText(card.front.plainText);
      setBackFormatted(card.back);
      setSelectedSectionId(card.sectionId);
      setTags(card.tags.join(', '));
      // Extract existing images from HTML
      const extractImgs = (html: string): string[] => {
        const imgs: string[] = [];
        const re = /<img[^>]+src="([^"]+)"/g;
        let m;
        while ((m = re.exec(html)) !== null) imgs.push(m[1]);
        return imgs;
      };
      setFrontImages(extractImgs(card.front.html));
      setBackImages(extractImgs(card.back.html));
    }
  }, [card]);

  /**
   * Set default section if available
   */
  useEffect(() => {
    if (!selectedSectionId && sections.length > 0) {
      setSelectedSectionId(sections[0].id);
    }
  }, [sections, selectedSectionId]);

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate front text
    if (!frontText.trim()) {
      newErrors.front = 'Front text is required';
    }

    // Validate back text
    if (!backFormatted.plainText.trim()) {
      newErrors.back = 'Back text is required';
    }

    // Validate section selection
    if (!selectedSectionId) {
      newErrors.section = 'Please select a section';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Parse tags from comma-separated string
      const tagArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Create CardInput object
      const cardInput: CardInput = {
        front: {
          plainText: frontText.trim(),
          html: frontText.trim() + frontImages.map(url => `<img src="${url}" alt="card image" style="max-width:100%;margin-top:8px;" />`).join('')
        },
        back: {
          plainText: backFormatted.plainText,
          html: backFormatted.html + backImages.map(url => `<img src="${url}" alt="card image" style="max-width:100%;margin-top:8px;" />`).join('')
        },
        sectionId: selectedSectionId,
        tags: tagArray
      };

      await onSave(cardInput);
    } catch (error) {
      console.error('Error saving card:', error);
      // Error handling is done by parent component
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle cancel button
   */
  const handleCancel = () => {
    onCancel();
  };

  /**
   * Handle front text change
   */
  const handleFrontChange = (value: string) => {
    setFrontText(value);
    // Clear error when user starts typing
    if (errors.front) {
      setErrors({ ...errors, front: undefined });
    }
  };

  /**
   * Handle back formatted text change
   */
  const handleBackChange = (value: FormattedText) => {
    setBackFormatted(value);
    // Clear error when user starts typing
    if (errors.back) {
      setErrors({ ...errors, back: undefined });
    }
  };

  /**
   * Handle section selection change
   */
  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSectionId(e.target.value);
    // Clear error when user selects a section
    if (errors.section) {
      setErrors({ ...errors, section: undefined });
    }
  };

  const isEditMode = !!card;

  return (
    <div className="card-editor">
      <div className="card-editor-header">
        <h2>{isEditMode ? 'Edit Card' : 'Create New Card'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="card-editor-form">
        {/* Front field - Conditionally use GreekInput or plain textarea */}
        <div className="form-group">
          {config.ui.includeGreekInput ? (
            <GreekInput
              value={frontText}
              onChange={handleFrontChange}
              label="Front (Question)"
              placeholder="Enter Greek text or question..."
              multiline={true}
              rows={3}
              required={true}
              id="card-front"
            />
          ) : (
            <>
              <label htmlFor="card-front" className="form-label">
                Front (Question)
                <span className="required-indicator">*</span>
              </label>
              <textarea
                id="card-front"
                className="form-input"
                value={frontText}
                onChange={(e) => handleFrontChange(e.target.value)}
                placeholder="Enter question..."
                rows={3}
                required
              />
            </>
          )}
          {errors.front && (
            <div className="error-message">{errors.front}</div>
          )}
          {/* Front image upload */}
          <div className="card-image-upload">
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => frontImageRef.current?.click()}>
              {uploadingFront ? 'Uploading…' : '+ Add image'}
            </button>
            <input ref={frontImageRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleImageFile(e, 'front')} />
            {frontImages.length > 0 && (
              <div className="card-image-previews">
                {frontImages.map((url, i) => (
                  <div key={i} className="card-image-preview-item">
                    <img src={url} alt={`front ${i + 1}`} />
                    <button type="button" onClick={() => setFrontImages(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Back field - Formatted text with toolbar */}
        <div className="form-group">
          <FormattingToolbar
            value={backFormatted}
            onChange={handleBackChange}
            label="Back (Answer)"
            placeholder="Enter answer with formatting..."
            required={true}
            id="card-back"
          />
          {errors.back && (
            <div className="error-message">{errors.back}</div>
          )}
          {/* Back image upload */}
          <div className="card-image-upload">
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => backImageRef.current?.click()}>
              {uploadingBack ? 'Uploading…' : '+ Add image'}
            </button>
            <input ref={backImageRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleImageFile(e, 'back')} />
            {backImages.length > 0 && (
              <div className="card-image-previews">
                {backImages.map((url, i) => (
                  <div key={i} className="card-image-preview-item">
                    <img src={url} alt={`back ${i + 1}`} />
                    <button type="button" onClick={() => setBackImages(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section selector */}
        <div className="form-group">
          <label htmlFor="card-section" className="form-label">
            Section
            <span className="required-indicator">*</span>
          </label>
          <select
            id="card-section"
            className="form-select"
            value={selectedSectionId}
            onChange={handleSectionChange}
            required
          >
            <option value="">Select a section...</option>
            {sections.map(section => (
              <option key={section.id} value={section.id}>
                {section.name} ({section.cardCount} cards)
              </option>
            ))}
          </select>
          {errors.section && (
            <div className="error-message">{errors.section}</div>
          )}
        </div>

        {/* Tags input */}
        <div className="form-group">
          <label htmlFor="card-tags" className="form-label">
            Tags (optional)
          </label>
          <input
            type="text"
            id="card-tags"
            className="form-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Enter tags separated by commas (e.g., vocabulary, grammar, verbs)"
          />
          <div className="form-hint">
            Separate multiple tags with commas
          </div>
        </div>

        {/* Action buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Card' : 'Create Card'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CardEditor;
